<script lang="ts" setup>
  import { useMutation } from '@tanstack/vue-query'

  import { type ContactDTO, useOtherService } from '~/core/services'
  import type { FetchErrorObj } from '~/core/types'

  const route = useRoute()
  const router = useRouter()
  const { displayToast } = useUiStore()
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)
  const { postContact } = useOtherService()

  const isPro = ref(false)
  const hasConsent = ref(false)

  const formValues = ref<ContactDTO>({
    email: null,
    message: null,
  })

  const radioItems = ref(['Particulier', 'Professionnel'])
  const radioIndex = ref(0)

  const consentLabel = `Je consens à la collecte de mes données par BOOKANDGOLF à des fins de Gestion de ma réservation / demande pour la durée nécessaire au traitement conformément à la <a href="/privacy-policy">Politique de confidentialité</a>. L'exercice de vos droits est possible à l'adresse <a href="mailto:dpo@groupeduval.com">dpo@groupeduval.com</a>`

  function onRadioInput(index: number) {
    radioIndex.value = index
    isPro.value = index !== 0
    reset()
    hasConsent.value = false
    formValues.value = {
      email: null,
      message: null,
    }
  }

  const { mutate: contact, isPending: isPendingContact, error, reset } = useMutation<unknown, FetchErrorObj<ContactDTO>, ContactDTO>({
    mutationFn: postContact,
    onSuccess: () => {
      displayToast(`Votre message a bien été envoyé`, 'success', 5000)
      router.push({
        path: route.path,
        query: {
          ...route.query,
          modal: undefined,
        },
      })
      formValues.value = {
        email: null,
        message: null,
      }
      hasConsent.value = false
    },
    onError: (res) => {
      displayToast(res.message, 'error', 5000)
    },
  })

  onMounted(() => {
    if (route.query.section === 'pro') {
      onRadioInput(1)
    }
  })
</script>

<template>
  <div class="c-contact-form">
    <h1>
      <SvgIcon
        icon="help"
        :width="matchDesktop ? 38 : 20"
        :height="matchDesktop ? 38 : 20"
      />
      <span>Contactez-nous</span>
    </h1>
    <form
      class="form"
      @submit.prevent="() => contact({...formValues, type: isPro ? 'pro' : 'individual'})"
    >
      <h2>VOUS ÊTES ?</h2>
      <InputRadioSelect
        :items="radioItems"
        :selected-index="radioIndex"
        @on-input="onRadioInput"
      />
      <InputBase
        v-if="isPro"
        v-model="formValues.name"
        placeholder=""
        name="name"
        type="text"
        label="Votre nom"
        required
        :errors="error?.errors?.name"
      />
      <InputBase
        v-if="isPro"
        v-model="formValues.golf_name"
        placeholder=""
        name="golf_name"
        type="text"
        label="Nom de votre golf"
        required
        :errors="error?.errors?.golf_name"
      />
      <InputBase
        v-model="formValues.email"
        placeholder=""
        name="email"
        type="email"
        label="Email"
        required
        :errors="error?.errors?.email"
      />
      <InputBase
        v-if="isPro"
        v-model="formValues.phone"
        placeholder=""
        name="phone"
        type="tel"
        label="Téléphone"
        required
        :errors="error?.errors?.phone"
      />
      <InputBase
        v-model="formValues.message"
        placeholder=""
        name="text"
        type="textarea"
        label="Message"
        required
        :errors="error?.errors?.message"
      />
      <InputCheckbox
          v-model="hasConsent"
          class="consent-checkbox"
          name="contact-consent"
          :label="consentLabel"
          required
      />
      <Button
        type="secondary"
        :disabled="isPendingContact"
        button-type="submit"
      >
        envoyer
      </Button>
    </form>
  </div>
</template>
<style>

.c-contact-form {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow-y: scroll;

  h1 {
    display: flex;
    flex-direction: row;
    margin: 1.5em;

    .c-icon {
      margin-right: 0.875em;
      color: var(--color-primary);
    }

    span {
      color: var(--color-secondary);
      font-family: Bariol, sans-serif;
      font-size: 1.5em;
      font-weight: bold;
    }
  }

  .form {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    padding: 0 1em 1em;
    margin: 0 auto;
    overflow-y: auto;

    .c-button {
      width: 100%;
      flex: 0 0 auto;
      margin-top: 2.25em;
      font-size: 1.5em;
      text-transform: uppercase;
    }

    .radio-select {
      display: flex;
      margin-top: 2.25em;

      span {
        position: relative;
        display: flex;
        align-items: center;
        margin-right: 0.5em;
        color: var(--color-secondary);
        cursor: pointer;
        font-family: Open-Sans, sans-serif;
        font-size: 1.125em;
        font-weight: 600;
        line-height: 1.33;
        opacity: 0.8;
        user-select: none;

        .dot {
          display: flex;
          width: 18px;
          height: 18px;
          border: 1px solid var(--color-secondary);
          border-radius: 100%;
          margin-right: 0.5em;
          content: "";
          opacity: 0.5;
        }

        &.active::after {
          position: absolute;
          display: flex;
          width: 8px;
          height: 8px;
          border-radius: 100%;
          margin: auto auto auto 5px;
          background-color: var(--color-primary);
          content: "";
        }
      }
    }

    h2 {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1.125em;
      font-weight: bold;
      letter-spacing: 1.44px;
      opacity: 0.8;
      text-transform: uppercase;
    }

    .c-input {
      margin-top: 1.5em;
    }

    .c-checkbox {
      margin-top: 1.5em;
    }

    .consent-checkbox .label-txt {
      color: var(--color-grey-light);
    }
  }

  @media screen and (width >= 1024px) {
    overflow: auto;

    h1 {
      margin: 0 0 2.5em;

      span {
        font-size: 2.5em;
      }
    }

    section {
      width: 25em;
      padding: 0 1em 0 0;
    }
  }
}
</style>
